import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { IMG } from "@/lib/site";
import Magnetic from "./Magnetic";

const LINES = [
  { txt: "Webseiten," },
  { txt: "die ", em: "Kunden", after: " bringen." },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } } };
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.83, 0, 0.17, 1] } },
};

export default function Hero({ ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section id="start" ref={ref} className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden bg-[#FAFAF7]">
      {/* faint editorial rules */}
      <div className="absolute inset-x-0 top-24 h-px bg-black/5" />
      <div className="absolute inset-x-0 bottom-16 h-px bg-black/5" />

      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-12 gap-6 lg:gap-10 items-end">
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-8">
          {/* eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 12 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="gold-hairline w-10" />
            <span className="font-mono-eyebrow text-[#0A0A0A]/70">Digitalagentur · Bern · Seit 2026</span>
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate={ready ? "show" : "hidden"}
            className="font-serif-editorial text-[15vw] sm:text-[12vw] md:text-[10rem] lg:text-[11.5rem] xl:text-[13rem] leading-[0.92] tracking-tight"
            data-testid="hero-headline"
          >
            {LINES.map((l, i) => (
              <span key={i} className="line-mask">
                <motion.span variants={line} className="line-inner">
                  {l.em ? (
                    <>
                      {l.txt}<em className="italic text-[#C9A961] font-normal">{l.em}</em>{l.after}
                    </>
                  ) : l.txt}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 md:mt-14 grid grid-cols-12 gap-6"
          >
            <p className="col-span-12 md:col-span-7 text-[#0A0A0A]/75 text-base md:text-lg leading-relaxed max-w-2xl">
              Professionelles Webdesign kombiniert mit Performance Marketing.
              Transparente Festpreise statt Überraschungen. Ein Team, ein Ansprechpartner,
              ein System, das Anfragen liefert.
            </p>
            <div className="col-span-12 md:col-span-5 flex flex-col sm:flex-row md:justify-end gap-3 md:items-end">
              <Magnetic strength={0.2}>
                <button data-testid="hero-cta-primary" onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })} className="btn-primary">
                  Kostenloses Erstgespräch <ArrowUpRight size={16} className="arrow" />
                </button>
              </Magnetic>
              <Magnetic strength={0.18}>
                <button data-testid="hero-cta-secondary" onClick={() => document.getElementById("partnerschaft")?.scrollIntoView({ behavior: "smooth" })} className="btn-ghost">
                  Pakete ansehen
                </button>
              </Magnetic>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-14 md:mt-20 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.22em] text-[#0A0A0A]/70"
          >
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9A961]" />Festpreise</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9A961]" />Keine versteckten Kosten</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9A961]" />Made in Bern</span>
          </motion.div>
        </motion.div>

        {/* Right image column */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 40 }}
          transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-4 relative"
        >
          <div className="relative aspect-[3/4] md:aspect-[4/5] lg:aspect-[3/4] overflow-hidden">
            <motion.img
              src={IMG.heroBern}
              alt="Berner Altstadt Architektur"
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ y: imgY, scale: imgScale }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/30 via-transparent to-transparent pointer-events-none" />
            <div className="absolute left-4 bottom-4 right-4 flex items-end justify-between text-[#FAFAF7]">
              <div>
                <div className="font-mono-eyebrow text-[#C9A961]">Standort</div>
                <div className="font-serif-editorial text-2xl mt-1">Bern, CH</div>
              </div>
              <div className="text-right">
                <div className="font-mono-eyebrow text-[#FAFAF7]/70">Koord.</div>
                <div className="font-mono-eyebrow mt-1">46.94° N · 7.44° E</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: ready ? 1 : 0 }} transition={{ delay: 1.3, duration: 0.8 }}
        className="mx-auto max-w-[1440px] px-5 md:px-10 mt-16 md:mt-24 flex items-center gap-3 text-[#0A0A0A]/60"
      >
        <ArrowDown size={16} className="text-[#C9A961]" />
        <span className="font-mono-eyebrow">Scrollen · Das komplette Kundengewinnungssystem</span>
      </motion.div>
    </section>
  );
}
